import React, { useState, useEffect } from "react";
import { Icon } from "@amzn/meridian";
import flashTokens from "@amzn/meridian-tokens/base/icon/flash"
import { useAppDispatch, useAppSelector } from "src/store/store";
import { setFlashlightEnabled } from "src/store/workflowSlice";
import "./FlashlightButton.scss";

type FlashlightButtonProps = {
  videoRef: React.RefObject<HTMLVideoElement>;
};

const FlashlightButton = ({ videoRef }: FlashlightButtonProps) => {
  const dispatch = useAppDispatch();
  // Last user-chosen state for this inspection (default off, reset on new/resumed inspection)
  const storedTorch = useAppSelector((state) => state.workflow.flashlightEnabled);
  const [torchEnabled, setTorchEnabled] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  // Check if torch is supported
  useEffect(() => {
    const checkTorchSupport = async () => {
      try {
        if (!videoRef.current?.srcObject) {
          setIsChecking(false);
          return;
        }

        const stream = videoRef.current.srcObject as MediaStream;
        const videoTrack = stream.getVideoTracks()[0];
        
        if (!videoTrack) {
          setTorchSupported(false);
          setIsChecking(false);
          return;
        }

        // Check if torch capability exists
        const capabilities = videoTrack.getCapabilities();
        const hasTorch = 'torch' in capabilities && capabilities.torch === true;
        
        setTorchSupported(hasTorch);
        // Re-apply the last user-chosen state on this (new) camera stream
        if (hasTorch && storedTorch) {
          try {
            await videoTrack.applyConstraints({
              // @ts-ignore - torch is not in TypeScript's MediaTrackConstraints type yet
              advanced: [{ torch: true }]
            });
            setTorchEnabled(true);
          } catch (e) {
            console.warn("Failed to restore flashlight state:", e);
          }
        }
        setIsChecking(false);
      } catch (error) {
        console.warn("Failed to check torch support:", error);
        setTorchSupported(false);
        setIsChecking(false);
      }
    };

    // Check after a short delay to ensure video stream is ready
    const timeout = setTimeout(checkTorchSupport, 500);
    
    return () => clearTimeout(timeout);
  }, [videoRef.current?.srcObject]);

  const toggleFlashlight = async () => {
    try {
      if (!videoRef.current?.srcObject) {
        return;
      }

      const stream = videoRef.current.srcObject as MediaStream;
      const videoTrack = stream.getVideoTracks()[0];
      
      if (!videoTrack) {
        return;
      }

      const newTorchState = !torchEnabled;
      
      // Apply torch constraint
      await videoTrack.applyConstraints({
        // @ts-ignore - torch is not in TypeScript's MediaTrackConstraints type yet
        advanced: [{ torch: newTorchState }]
      });
      
      setTorchEnabled(newTorchState);
      dispatch(setFlashlightEnabled(newTorchState));
    } catch (error) {
      console.error("Failed to toggle flashlight:", error);
      // Reset state if toggle failed
      setTorchEnabled(false);
    }
  };

  // Don't render if torch is not supported or still checking
  if (isChecking || !torchSupported) {
    return null;
  }

  return (
   <div className="flashlight-button-container">
      <button
        type="button"
        onClick={toggleFlashlight}
        className={`flashlight-button ${torchEnabled ? 'flashlight-button--active' : ''}`}
        aria-label={torchEnabled ? "Turn flashlight off" : "Turn flashlight on"}
      >
        <Icon
          color={torchEnabled ? 'primary' : 'inverted'}
          tokens={flashTokens}
        />
      </button>
    </div>
  );
};

export default FlashlightButton;